import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type AdjustmentType = 'ADJUSTMENT_IN' | 'ADJUSTMENT_OUT' | 'INITIAL_STOCK' | 'SCRAP';

interface StockAdjustmentFormData {
  productId: string;
  warehouseId: string;
  quantity: number;
  type: AdjustmentType;
  unitCost?: number;
  notes: string;
}

interface StockAdjustmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedProduct?: {
    id: string;
    name: string;
    code: string;
  } | null;
  preselectedWarehouse?: {
    id: string;
    name: string;
  } | null;
}

export function StockAdjustmentModal({
  open,
  onOpenChange,
  preselectedProduct,
  preselectedWarehouse,
}: StockAdjustmentModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const utils = trpc.useUtils();
  const [confirmationChecked, setConfirmationChecked] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<StockAdjustmentFormData>({
    defaultValues: {
      productId: '',
      warehouseId: '',
      quantity: 1,
      type: 'ADJUSTMENT_IN',
      unitCost: undefined,
      notes: '',
    },
  });

  const selectedType = watch('type');
  const selectedProductId = watch('productId');
  const selectedWarehouseId = watch('warehouseId');

  // Fetch products and warehouses
  const { data: productsData } = trpc.tradingProduct.list.useQuery({ search: '' });
  const { data: warehousesData } = trpc.warehouse.list.useQuery({});

  // Fetch current stock level for selected product/warehouse
  const { data: currentStock } = trpc.stockLevel.byProductWarehouse.useQuery(
    { productId: selectedProductId, warehouseId: selectedWarehouseId },
    { enabled: !!selectedProductId && !!selectedWarehouseId }
  );

  const products = productsData?.products ?? [];
  const warehouses = warehousesData ?? [];

  useEffect(() => {
    if (open) {
      setConfirmationChecked(false);
      reset({
        productId: preselectedProduct?.id || '',
        warehouseId: preselectedWarehouse?.id || '',
        quantity: 1,
        type: 'ADJUSTMENT_IN',
        unitCost: undefined,
        notes: '',
      });
    }
  }, [open, preselectedProduct, preselectedWarehouse, reset]);

  const adjustMutation = trpc.stockLevel.adjust.useMutation({
    onSuccess: (data) => {
      showSuccessToast(t('inventory.adjustment.success', { movementNumber: data.movementNumber }));
      utils.stockLevel.list.invalidate();
      utils.stockLevel.byProductWarehouse.invalidate();
      utils.stockMovement.list.invalidate();
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const onSubmit = (data: StockAdjustmentFormData) => {
    if (!confirmationChecked) {
      showErrorToast(t('inventory.adjustment.confirmRequired'));
      return;
    }

    adjustMutation.mutate({
      productId: data.productId,
      warehouseId: data.warehouseId,
      quantity: data.quantity,
      type: data.type,
      unitCost: data.unitCost,
      notes: data.notes || undefined,
    });
  };

  const isLoading = adjustMutation.isPending;
  const requiresCost = selectedType === 'ADJUSTMENT_IN' || selectedType === 'INITIAL_STOCK';

  const getAdjustmentTypeLabel = (type: AdjustmentType) => {
    switch (type) {
      case 'ADJUSTMENT_IN':
        return t('inventory.adjustment.types.adjustmentIn');
      case 'ADJUSTMENT_OUT':
        return t('inventory.adjustment.types.adjustmentOut');
      case 'INITIAL_STOCK':
        return t('inventory.adjustment.types.initialStock');
      case 'SCRAP':
        return t('inventory.adjustment.types.scrap');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('inventory.adjustment.title')}</DialogTitle>
          <DialogDescription>{t('inventory.adjustment.description')}</DialogDescription>
        </DialogHeader>

        {/* Warning Banner */}
        <div className="flex items-start gap-3 p-4 border border-amber-200 bg-amber-50">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-amber-800">{t('inventory.adjustment.warning')}</p>
            <p className="text-amber-700 mt-1">{t('inventory.adjustment.warningDesc')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Product Selection */}
          <div className="space-y-2">
            <Label>{t('inventory.adjustment.product')} *</Label>
            <Combobox
              options={products.map((p) => ({
                value: p.id,
                label: `${p.name} (${p.fullCode})`,
              }))}
              value={selectedProductId}
              onValueChange={(value) => setValue('productId', value)}
              placeholder={t('inventory.adjustment.selectProduct')}
              searchPlaceholder={t('inventory.adjustment.searchProduct')}
              disabled={!!preselectedProduct}
            />
            {errors.productId && (
              <p className="text-xs text-red-500">{tCommon('validation.required')}</p>
            )}
          </div>

          {/* Warehouse Selection */}
          <div className="space-y-2">
            <Label>{t('inventory.adjustment.warehouse')} *</Label>
            <Combobox
              options={warehouses.map((w) => ({
                value: w.id,
                label: `${w.name} (${w.code})`,
              }))}
              value={selectedWarehouseId}
              onValueChange={(value) => setValue('warehouseId', value)}
              placeholder={t('inventory.adjustment.selectWarehouse')}
              searchPlaceholder={t('inventory.adjustment.searchWarehouse')}
              disabled={!!preselectedWarehouse}
            />
            {errors.warehouseId && (
              <p className="text-xs text-red-500">{tCommon('validation.required')}</p>
            )}
          </div>

          {/* Current Stock Info */}
          {currentStock && (
            <div className="p-3 bg-muted/50 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('inventory.adjustment.currentStock')}</span>
                <span className="font-medium">{currentStock.quantity}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('inventory.adjustment.currentAvailable')}</span>
                <span className="font-medium">{currentStock.available}</span>
              </div>
              {currentStock.averageCost && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('inventory.adjustment.currentAvgCost')}</span>
                  <span className="font-medium">
                    {/* StockLevel.averageCost is a TRY book value (frozen at receipt) */}
                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(currentStock.averageCost)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Adjustment Type */}
          <div className="space-y-2">
            <Label>{t('inventory.adjustment.type')} *</Label>
            <Select
              value={selectedType}
              onValueChange={(value) => setValue('type', value as AdjustmentType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ADJUSTMENT_IN">{getAdjustmentTypeLabel('ADJUSTMENT_IN')}</SelectItem>
                <SelectItem value="ADJUSTMENT_OUT">{getAdjustmentTypeLabel('ADJUSTMENT_OUT')}</SelectItem>
                <SelectItem value="INITIAL_STOCK">{getAdjustmentTypeLabel('INITIAL_STOCK')}</SelectItem>
                <SelectItem value="SCRAP">{getAdjustmentTypeLabel('SCRAP')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quantity and Cost */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="quantity">{t('inventory.adjustment.quantity')} *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                {...register('quantity', { required: true, valueAsNumber: true, min: 1 })}
              />
              {errors.quantity && (
                <p className="text-xs text-red-500">{tCommon('validation.required')}</p>
              )}
            </div>

            {requiresCost && (
              <div className="space-y-2">
                {/* Booked as TRY batch cost server-side — label the currency */}
                <Label htmlFor="unitCost">{t('inventory.adjustment.unitCost')} (TRY) *</Label>
                <Input
                  id="unitCost"
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('unitCost', { valueAsNumber: true })}
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('inventory.adjustment.notes')} *</Label>
            <Textarea
              id="notes"
              {...register('notes', { required: true })}
              placeholder={t('inventory.adjustment.notesPlaceholder')}
              rows={3}
            />
            {errors.notes && (
              <p className="text-xs text-red-500">{t('inventory.adjustment.notesRequired')}</p>
            )}
            <p className="text-xs text-muted-foreground">{t('inventory.adjustment.notesHint')}</p>
          </div>

          {/* Confirmation Checkbox */}
          <div className="flex items-start gap-3 p-3 border border-red-200 bg-red-50">
            <input
              type="checkbox"
              id="confirmation"
              checked={confirmationChecked}
              onChange={(e) => setConfirmationChecked(e.target.checked)}
              className="mt-1"
            />
            <label htmlFor="confirmation" className="text-sm text-red-800 cursor-pointer">
              {t('inventory.adjustment.confirmText')}
            </label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !confirmationChecked}
              variant="destructive"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('inventory.adjustment.apply')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
